import "./PPCSideBar.scss";
import "./PPCSideBarMobile.scss";
import React, { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import SBCustomRangeSlider from "./SBCustomRangeSlider/SBCustomRangeSlider";
import StarReview from "./Component/StarReview";
import { FaEraser, FaTimes } from "react-icons/fa";

function PPCSideBar({ setFilterData }) {
  const startYear = 2015;
  const dropdownRef = useRef(null);
  const currentYear = new Date().getFullYear();

  // Toggle sections
  const [isProdTypeActive, setIsProdTypeActive] = useState(false);
  const [isOrderTypeActive, setIsOrderTypeActive] = useState(false);
  const [isAnimeSeriesActive, setIsAnimeSeriesActive] = useState(false);
  const [isPriceRangeActive, setIsPriceRangeActive] = useState(false);
  const [isReviewActive, setIsReviewActive] = useState(false);
  const [isManufacturerActive, setIsManufacturerActive] = useState(false);

  // Data lists
  const [categories, setCategories] = useState([]);
  const [animeSeries, setAnimeSeries] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);

  // Filter states
  const [selectedOrderType, setSelectedOrderType] = useState("all");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedAnimeSeries, setSelectedAnimeSeries] = useState(null);
  const [selectedManufacturers, setSelectedManufacturers] = useState([]);
  const [selectedStar, setSelectedStar] = useState(0);
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [selectedYear, setSelectedYear] = useState("all");
  const [maxRange, setMaxRange] = useState(0);
  const [minRange, setMinRange] = useState(0);
  const [highest, setHighest] = useState(null);

  // Anime dropdown states
  const [searchAnime, setSearchAnime] = useState("");
  const [showAnimeDropdown, setShowAnimeDropdown] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(0);

  const [dataLoaded, setDataLoaded] = useState(false);

  const { state } = useLocation();

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BASE_URL}api/product/add-get-foreign`
        );
        const data = await res.json();
        setCategories(data.categories);
        setManufacturers(data.manufacturers);
        setAnimeSeries(data.animeSeries);

        const resPrice = await fetch(
          `${process.env.REACT_APP_BASE_URL}api/product/get-highest-price`
        );
        const priceData = await resPrice.json();
        const roundSoft = (num) => {
          if (num < 10) return num;
          const digits = Math.floor(Math.log10(num));
          const factor = 10 ** (digits - 1);
          return Math.ceil(num / factor) * factor;
        };
        const rawHighest =
          priceData.highestOrigPrice > priceData.highestSalePrice
            ? priceData.highestOrigPrice
            : priceData.highestSalePrice;

        const roundedHighest = roundSoft(rawHighest);
        setHighest(roundedHighest);
        setMaxRange(roundedHighest);

        setDataLoaded(true); // mark as ready
      } catch (err) {
        console.error("Failed to fetch data:", err);
      }
    };

    fetchData();
  }, []);

  // Initialize filters after data is loaded
  useEffect(() => {
    if (!dataLoaded || animeSeries.length === 0 || manufacturers.length === 0)
      return;

    // 1️⃣ Apply router state
    if (state?.manufacturerId) setSelectedManufacturers([state.manufacturerId]);
    else setSelectedManufacturers([]); // reset if none

    if (state?.catId) setSelectedCategories([state.catId]);
    else setSelectedCategories([]);

    if (state?.animeName) {
      const match = animeSeries.find((a) => a.AS_Name === state.animeName);
      if (match) {
        setSelectedAnimeSeries(match.AS_ID);
        setSearchAnime(match.AS_Name);
      } else {
        setSelectedAnimeSeries(null);
        setSearchAnime("");
      }
    } else {
      setSelectedAnimeSeries(null);
      setSearchAnime("");
    }

    if (state?.filterVal) setSelectedOrderType(state.filterVal);
    else setSelectedOrderType("all");

    // 2️⃣ Apply saved localStorage filters
    const saved = JSON.parse(localStorage.getItem("redirectAfterLogin"));
    if (saved?.filterData) {
      const f = saved.filterData;
      setSelectedCategories(f.categories || []);
      setSelectedManufacturers(f.manufacturers || []);
      setSelectedStar(f.star || 0);
      setSelectedMonth(f.month ?? "all");
      setSelectedYear(f.year ?? "all");
      setMinRange(f.minPrice || 0);
      setMaxRange(f.maxPrice || highest || 0);

      if (Array.isArray(f.animeSeries) && f.animeSeries.length > 0) {
        const matched = animeSeries.find((a) => a.AS_ID === f.animeSeries[0]);
        if (matched) setSearchAnime(matched.AS_Name);
        setSelectedAnimeSeries(f.animeSeries[0]);
      }
    }
  }, [dataLoaded, animeSeries, manufacturers, highest, state]);

  // Update parent whenever filters change
  useEffect(() => {
    if (!dataLoaded) return;

    setFilterData({
      categories: selectedCategories,
      animeSeries: selectedAnimeSeries ? [selectedAnimeSeries] : [],
      manufacturers: selectedManufacturers,
      star: selectedStar,
      month: selectedMonth === "all" ? null : Number(selectedMonth),
      year: selectedYear === "all" ? null : Number(selectedYear),
      minPrice: minRange,
      maxPrice: maxRange,
      orderType: selectedOrderType === "All" ? null : selectedOrderType,
    });
  }, [
    dataLoaded,
    selectedCategories,
    selectedAnimeSeries,
    selectedManufacturers,
    selectedStar,
    selectedMonth,
    selectedYear,
    minRange,
    maxRange,
    selectedOrderType,
  ]);

  // Toggle category/manufacturer
  const toggleCategory = (id) =>
    setSelectedCategories((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  const toggleManufacturer = (id) =>
    setSelectedManufacturers((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );

  // Clear filters
  const handleClearFilters = () => {
    setSelectedCategories([]);
    setSelectedAnimeSeries(null);
    setSearchAnime("");
    setSelectedManufacturers([]);
    setSelectedStar(0);
    setSelectedMonth("all");
    setSelectedYear("all");
    setMinRange(0);
    setMaxRange(highest || 0);
    setSelectedOrderType("all");

    setFilterData({
      categories: [],
      animeSeries: [],
      manufacturers: [],
      star: 0,
      month: null,
      year: null,
      minPrice: 0,
      maxPrice: highest || 0,
      orderType: null,
    });
  };

  // Anime dropdown keyboard handling
  const filteredAnime = animeSeries.filter((item) =>
    item.AS_Name.toLowerCase().includes(searchAnime.toLowerCase())
  );

  const handleKeyDown = (e) => {
    if (!showAnimeDropdown || filteredAnime.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocusedIndex((prev) =>
        prev < filteredAnime.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusedIndex((prev) =>
        prev > 0 ? prev - 1 : filteredAnime.length - 1
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      const selected = filteredAnime[focusedIndex];
      if (selected) {
        setSelectedAnimeSeries(selected.AS_ID);
        setSearchAnime(selected.AS_Name);
        setShowAnimeDropdown(false);
      }
    } else if (e.key === "Tab") {
      setShowAnimeDropdown(false);
    }
  };

  useEffect(() => {
    if (dropdownRef.current && focusedIndex !== -1) {
      const listItems = dropdownRef.current.querySelectorAll(".anime-item");
      const focusedItem = listItems[focusedIndex];
      if (focusedItem)
        focusedItem.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [focusedIndex]);

  return (
    <div className="PPCSB-container">
      <div className="sb-title-wrapper">FILTERS</div>
      <span className="sb-clear" onClick={handleClearFilters}>
        <FaEraser /> CLEAR
      </span>

      {/* Product Type */}
      <div className="sb-prod-type-wrapper">
        <div
          className={`prod-type-header ${isProdTypeActive ? "active" : ""}`}
          onClick={() => setIsProdTypeActive(!isProdTypeActive)}
        >
          <span>PRODUCT TYPE</span>
          <div className="icon"></div>
        </div>
        <div className="prod-type-content">
          {categories.map((item) => (
            <span
              key={item.PC_ID}
              className={`prod-type-item ${
                selectedCategories.includes(item.PC_ID) ? "selected" : ""
              }`}
              onClick={() => toggleCategory(item.PC_ID)}
            >
              {item.PC_Name}
            </span>
          ))}
        </div>
      </div>

      {/* Order Type */}
      <div className="sb-prod-type-wrapper">
        <div
          className={`prod-type-header ${isOrderTypeActive ? "active" : ""}`}
          onClick={() => setIsOrderTypeActive(!isOrderTypeActive)}
        >
          <span>ORDER TYPE</span>
          <div className="icon"></div>
        </div>
        <div className="prod-type-content">
          <select
            value={selectedOrderType}
            onChange={(e) => setSelectedOrderType(e.target.value.toLowerCase())}
          >
            <option value="all">All</option>
            <option value="onhand">ON HAND</option>
            <option value="preorder">PREORDER</option>
          </select>
        </div>
      </div>

      {/* Anime Series */}
      <div className="sb-prod-type-wrapper">
        <div
          className={`prod-type-header ${isAnimeSeriesActive ? "active" : ""}`}
          onClick={() => setIsAnimeSeriesActive(!isAnimeSeriesActive)}
        >
          <span>ANIME SERIES</span>
          <div className="icon"></div>
        </div>
        <div className="prod-type-content">
          <div className="anime-dropdown">
            <input
              type="text"
              className="anime-input"
              placeholder="Search anime series..."
              value={searchAnime}
              onChange={(e) => {
                setSearchAnime(e.target.value);
                setFocusedIndex(0);
              }}
              onFocus={() => setShowAnimeDropdown(true)}
              onBlur={() => setTimeout(() => setShowAnimeDropdown(false), 150)}
              onKeyDown={handleKeyDown}
            />
            <span
              className={`clear ${searchAnime ? "active" : ""}`}
              onClick={() => {
                setSelectedAnimeSeries(null);
                setSearchAnime("");
              }}
            >
              <FaTimes />
            </span>
            {showAnimeDropdown && (
              <div className="anime-dropdown-list" ref={dropdownRef}>
                {filteredAnime.length > 0 ? (
                  filteredAnime.map((item, index) => (
                    <div
                      key={item.AS_ID}
                      className={`anime-item ${
                        selectedAnimeSeries === item.AS_ID ? "selected" : ""
                      } ${index === focusedIndex ? "focused" : ""}`}
                      onMouseDown={() => {
                        setSelectedAnimeSeries(item.AS_ID);
                        setSearchAnime(item.AS_Name);
                        setShowAnimeDropdown(false);
                      }}
                    >
                      {item.AS_Name}
                    </div>
                  ))
                ) : (
                  <div className="anime-item no-result">No result found</div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Price Range */}
      <div className="sb-prod-type-wrapper">
        <div
          className={`prod-type-header ${isPriceRangeActive ? "active" : ""}`}
          onClick={() => setIsPriceRangeActive(!isPriceRangeActive)}
        >
          <span>PRICE RANGE</span>
          <div className="icon"></div>
        </div>
        <div className="prod-type-content">
          <SBCustomRangeSlider
            min={0}
            max={highest}
            setMinRange={setMinRange}
            setMaxRange={setMaxRange}
          />
        </div>
      </div>

      {/* Ratings */}
      {/* RATINGS */}
      <div className="sb-prod-type-wrapper">
        <div
          className={`prod-type-header ${isReviewActive ? "active" : ""}`}
          onClick={() => setIsReviewActive(!isReviewActive)}
        >
          <span>RATINGS</span>
          <div className="icon"></div>
        </div>
        <div className="prod-type-content">
          <div className="prod-star-container">
            <StarReview
              setSelectedStar={setSelectedStar}
              selectedStar={selectedStar}
            />
          </div>
        </div>
      </div>

      {/* Manufacturer */}
      <div className="sb-prod-type-wrapper">
        <div
          className={`prod-type-header ${isManufacturerActive ? "active" : ""}`}
          onClick={() => setIsManufacturerActive(!isManufacturerActive)}
        >
          <span>MANUFACTURER</span>
          <div className="icon"></div>
        </div>
        <div className="prod-type-content">
          <div className="prod-manufacturer-wrapper">
            {manufacturers.map((item) => (
              <img
                key={item.PM_ID}
                src={`${process.env.REACT_APP_BASE_URL}Assets/Manufacturer/${item.PM_Name}/${item.PM_Logopath}`}
                className={
                  selectedManufacturers.includes(item.PM_ID) ? "selected" : ""
                }
                onClick={() => toggleManufacturer(item.PM_ID)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PPCSideBar;
